/**
 * Date utilities using date-fns library
 */
import { differenceInYears, format, isValid, parse } from "date-fns";

/**
 * Format a date to MM/DD/YYYY
 */
export function formatDateMMDDYYYY(date: Date): string {
  return format(date, "MM/dd/yyyy");
}

/**
 * Format a date string to a display format with age
 * Returns something like "January 1, 1990 (33 yrs)"
 */
export function formatDateWithAge(dateString: string): string | null {
  try {
    const date = parse(dateString, "MM/dd/yyyy", new Date());
    if (!isValid(date)) {
      return null;
    }

    const age = calculateAge(date);
    return `${format(date, "MMMM d, yyyy")} (${age} yrs)`;
  } catch {
    return null;
  }
}

/**
 * Calculate age from date of birth
 */
export function calculateAge(dateOfBirth: Date): number {
  return differenceInYears(new Date(), dateOfBirth);
}

/**
 * Format date to YYYY-MM-DD format for API compatibility
 */
export const formatDateToYYYYMMDD = (dateString: string): string => {
  // If the date is already in YYYY-MM-DD format, return it
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  // Try to parse the date from MM/DD/YYYY format
  try {
    const parsedDate = parse(dateString, "MM/dd/yyyy", new Date());
    if (!isValid(parsedDate)) {
      return dateString; // Return original if invalid
    }

    return format(parsedDate, "yyyy-MM-dd");
  } catch {
    return dateString; // Return original if parsing fails
  }
};

/**
 * Fixes date formatting issues especially for 2-digit and 3-digit years
 *
 * @param dateString - String date in MM/DD/YYYY format (can have partial years)
 * @returns Formatted date with age or null if invalid
 */
export function fixDateFormatting(dateString: string): string | null {
  if (!dateString || dateString.length < 8) return null;

  // Verify the format is MM/DD/YYYY with / as the separator
  if (!/^\d{1,2}\/\d{1,2}\/\d{1,4}$/.test(dateString)) return null;

  const dateParts = dateString.split("/");
  if (dateParts.length !== 3) return null;

  const month = parseInt(dateParts[0], 10);
  const day = parseInt(dateParts[1], 10);
  let year = dateParts[2];

  // Basic validation for month and day values
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  // Handle 2 or 3 digit years
  if (year.length < 4) {
    // For 2-digit years, use sliding window approach
    if (year.length === 2) {
      const currentYear = new Date().getFullYear();
      const century = Math.floor(currentYear / 100) * 100;
      const yearNum = parseInt(year, 10);

      // If year is greater than current year's last 2 digits, assume previous century
      if (yearNum > currentYear % 100) {
        year = `${century - 100 + yearNum}`;
      } else {
        year = `${century + yearNum}`;
      }
    }
    // For 3-digit years, assume they meant a 4-digit year with missing first digit
    else if (year.length === 3) {
      // Add leading "1" for better historical date handling
      year = `1${year}`;
    }
    // For 1-digit years, return null as it's too ambiguous
    else {
      return null;
    }
  } else if (year.length > 4) {
    // Reject years with more than 4 digits
    return null;
  }

  // Reject year 0000
  if (parseInt(year, 10) === 0) return null;

  try {
    const parsedDate = parse(
      `${dateParts[0]}/${dateParts[1]}/${year}`,
      "MM/dd/yyyy",
      new Date(),
    );

    // Extra validation to catch dates that parse but aren't valid
    // e.g., Feb 30, Apr 31, etc.
    if (!isValid(parsedDate)) return null;

    const age = differenceInYears(new Date(), parsedDate);
    return `${format(parsedDate, "MMMM d, yyyy")} (${age} yrs)`;
  } catch {
    return null;
  }
}

/**
 * Gets the month name from a specified number of months ago.
 * @param monthsAgo - The number of months to go back from the current date.
 * @param includeYear - Whether to include the year (e.g., "August 2025" vs "August").
 * @returns The full name of the month, optionally with year.
 */
export function getMonthNameMonthsAgo(monthsAgo: number, includeYear = false): string {
  const date = new Date();
  date.setMonth(date.getMonth() - monthsAgo);
  const options: Intl.DateTimeFormatOptions = { month: "long" };
  if (includeYear) {
    options.year = "numeric";
  }
  return date.toLocaleString("default", options);
}
